/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/currency';

interface PerUnitSubtitleProps {
    /** Per-unit current price ($100.00 in `($100.00 each)`). */
    unitPrice: number;
    /**
     * Optional max per-unit price — when supplied AND greater than `unitPrice`,
     * the subtitle renders as `(<min> – <max> each)` (legacy
     * `producttile_pricing.isml` price-range branch).
     */
    unitMaxPrice?: number;
    currency: string;
    className?: string;
}

/**
 * Legacy `org_ashcomm` `<span class="price-set">($X.XX each)</span>` parity —
 * the small grey subtitle that sits directly under the headline price on PLP
 * tiles and PDP when a product is sold as a "set of multiples"
 * (`minOrderQuantity > 1 && minOrderQuantity === stepQuantity`).
 *
 * Presentation-only — the parent (`<ProductPrice>`) owns the math (the unit
 * vs. multiplied-set price split) and the gating condition (`setOfMultiples`
 * + `showPerUnitSubtitle`). This component just formats one or two numbers as
 * currency and wraps them in the locale-aware "(... each)" template.
 *
 * Mirrors the legacy markup hooks:
 * - DOM hook → `data-testid="product-price-per-unit"` (+ shopper-visible
 *   subtitle is the only place a numeric per-unit price is rendered, so tests
 *   can target it directly without a class prefix).
 * - Typography → small muted text (`text-xs text-muted-foreground`) to match
 *   the legacy `.price-set` SCSS rule
 *   (`font-size: $label-font-size; font-weight: $font-wt-thin`).
 */
export function PerUnitSubtitle({ unitPrice, unitMaxPrice, currency, className }: PerUnitSubtitleProps): ReactElement {
    const { t, i18n } = useTranslation('product');
    const isRange = typeof unitMaxPrice === 'number' && unitMaxPrice > unitPrice;
    const min = formatCurrency(unitPrice, i18n.language, currency);
    const max = isRange ? formatCurrency(unitMaxPrice, i18n.language, currency) : undefined;
    const text = isRange ? t('price.perUnitSubtitleRange', { min, max }) : t('price.perUnitSubtitle', { price: min });
    // A11y label drops the parentheses for cleaner SR pronunciation.
    const ariaLabel = isRange ? t('price.rangeA11y', { min, max }) : t('price.perUnitSubtitleA11y', { price: min });
    return (
        <span
            data-testid="product-price-per-unit"
            className={cn('text-xs leading-4 text-muted-foreground', className)}
            aria-label={ariaLabel}>
            {text}
        </span>
    );
}

export default PerUnitSubtitle;
